/**
 * BatchStatusBadge Component
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 3: Batch Receipt (Shipment Entry)
 *
 * Displays the status of an inventory batch with appropriate styling.
 * Statuses: available (green), in_use (amber), depleted (grey)
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Circle, CircleDot, CircleOff } from 'lucide-react'

/**
 * Status configuration for styling and labels
 */
const STATUS_CONFIG = {
  available: {
    label: 'Available',
    variant: 'default',
    className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200',
    icon: Circle,
  },
  in_use: {
    label: 'In Use',
    variant: 'default',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200',
    icon: CircleDot,
  },
  depleted: {
    label: 'Depleted',
    variant: 'secondary',
    className: 'bg-slate-100 text-slate-600 hover:bg-slate-100 border-slate-200',
    icon: CircleOff,
  },
}

/**
 * Get the configuration for a status
 * @param {string} status - Batch status
 * @returns {Object} Status configuration
 */
export function getStatusConfig(status) {
  return STATUS_CONFIG[status] || STATUS_CONFIG.available
}

/**
 * Get the label for a status
 * @param {string} status - Batch status
 * @returns {string} Status label
 */
export function getStatusLabel(status) {
  return getStatusConfig(status).label
}

/**
 * BatchStatusBadge Component
 *
 * @param {Object} props
 * @param {string} props.status - Batch status (available, in_use, depleted)
 * @param {boolean} props.showIcon - Whether to show the status icon (default: false)
 * @param {string} props.size - Badge size ('sm' | 'md') (default: 'md')
 * @param {string} props.className - Additional CSS classes
 */
export default function BatchStatusBadge({
  status,
  showIcon = false,
  size = 'md',
  className
}) {
  const config = getStatusConfig(status)
  const Icon = config.icon

  return (
    <Badge
      variant={config.variant}
      className={cn(
        'font-medium border',
        config.className,
        size === 'sm' && 'text-xs px-1.5 py-0',
        className
      )}
    >
      {showIcon && (
        <Icon className={cn(
          'mr-1',
          size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'
        )} />
      )}
      {config.label}
    </Badge>
  )
}
